/**
 * react-markdown renderers shared by every assistant message.
 *
 * Two things here are version-sensitive and worth knowing before editing:
 *
 * - **Fenced code blocks are handled on `pre`, not `code`.** react-markdown
 *   dropped the `inline` prop in v9, and `className` cannot tell the cases
 *   apart either: a fence with no language and inline code both arrive with no
 *   className. `pre` only ever wraps a real block, so overriding it is the one
 *   unambiguous hook. `code` therefore renders the inline form only.
 *
 * - **Internal links become router `Link`s.** The model is told to emit paths
 *   like `/ModelManagement`; a plain `<a>` would full-page reload the SPA and
 *   throw away the conversation. Only the five state-free routes are reachable
 *   this way — the labeling studios need `location.state` and are banned in the
 *   system prompt.
 */
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

/** Recursively pull the plain text out of a React children tree. */
export const nodeText = (node) => {
  if (node === null || node === undefined || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (node.props?.children !== undefined) return nodeText(node.props.children);
  return '';
};

const isInternalPath = (href) => href && href.startsWith('/') && !href.startsWith('//');

/** Fenced code block with its own copy button. */
const CodeBlock = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const text = nodeText(children);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      // Clipboard needs a secure context; the text is still selectable.
      console.warn('Copy failed', error);
    }
  }, [text]);

  return (
    <div className="lf-md-code">
      <button
        type="button"
        className="lf-md-code__copy"
        onClick={copy}
        aria-label={copied ? 'Copied' : 'Copy code'}
        title={copied ? 'Copied' : 'Copy code'}
      >
        <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
      </button>
      <pre className="lf-md-code__pre">{children}</pre>
    </div>
  );
};

/**
 * Build the renderer map.
 *
 * `onNavigate` fires before an internal link is followed so the page can close
 * a mobile drawer; the navigation itself stays with the router.
 */
export const buildMarkdownComponents = ({ onNavigate } = {}) => ({
  // `node` is destructured out of every renderer below and thrown away.
  // react-markdown passes the mdast node as a prop, and spreading it onto a DOM
  // element makes React warn about an unknown attribute on every link and every
  // inline code span in an answer.
  a: ({ href, children, node, ...props }) => {
    if (isInternalPath(href)) {
      return (
        <Link to={href} className="lf-md-link" onClick={onNavigate} {...props}>
          {children}
          <i className="fa fa-angle-right lf-md-link__chevron" aria-hidden="true" />
        </Link>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="lf-md-link"
        {...props}
      >
        {children}
      </a>
    );
  },

  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,

  // Inline code only — see the note at the top of the file.
  code: ({ children, node, ...props }) => (
    <code className="lf-md-inline-code" {...props}>
      {children}
    </code>
  ),

  // Headings inside a chat reply are demoted: an <h1> from the model must not
  // outrank the page's own heading, visually or for a screen reader.
  h1: ({ children }) => <h3 className="lf-md-h lf-md-h--1">{children}</h3>,
  h2: ({ children }) => <h4 className="lf-md-h lf-md-h--2">{children}</h4>,
  h3: ({ children }) => <h5 className="lf-md-h lf-md-h--3">{children}</h5>,
  h4: ({ children }) => <h6 className="lf-md-h lf-md-h--4">{children}</h6>,

  ul: ({ children }) => <ul className="lf-md-list">{children}</ul>,
  ol: ({ children }) => <ol className="lf-md-list lf-md-list--ordered">{children}</ol>,
  blockquote: ({ children }) => <blockquote className="lf-md-quote">{children}</blockquote>,
  hr: () => <hr className="lf-md-rule" />,
});

export default buildMarkdownComponents;
